import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetTaxRulesListDocument,
  CreateTaxRuleDocument,
  type GetTaxRulesListQuery,
  type CreateTaxRuleMutation,
  type CreateTaxRuleMutationVariables,
} from '@/generated/root-types';
import { DataTable } from '@gastrosoft/shared/ui';
import { useNotification } from '@/app/contexts/NotificationContext';
import { useAuth } from '@/app/auth/AuthContext';
import { isSuperAdmin } from '@/utils/roles';
import { GRAPHQL_MAX_PAGE_SIZE } from '@/utils/constants';

interface Row extends Record<string, unknown> {
  id: string;
  name: string;
  taxRate: number;
  isActive: boolean;
  isDefault: boolean;
  printText: string | null;
  createdAt: string;
  updatedAt: string;
}

export default function TaxRules() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatPct = (n: number) =>
    new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 }).format(n / 100);
  const formatDateTime = (iso: string) => new Date(iso).toLocaleString(locale);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [taxRate, setTaxRate] = useState('19');
  const [printText, setPrintText] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);

  const { data, loading, refetch } = useQuery<GetTaxRulesListQuery>(GetTaxRulesListDocument, {
    variables: { first: GRAPHQL_MAX_PAGE_SIZE },
  });

  const [createRule, { loading: creating }] = useMutation<
    CreateTaxRuleMutation,
    CreateTaxRuleMutationVariables
  >(CreateTaxRuleDocument);

  const nodes = data?.taxRules?.nodes ?? [];
  const rows: Row[] = nodes.map((n) => ({
    id: n.id,
    name: n.name,
    taxRate: n.taxRate,
    isActive: n.isActive,
    isDefault: n.isDefault,
    printText: n.printText ?? null,
    createdAt: n.createdAt,
    updatedAt: n.updatedAt,
  }));

  const columns: GridColDef[] = [
    { field: 'name', headerName: t('common.name'), flex: 1, minWidth: 160 },
    {
      field: 'taxRate',
      headerName: t('masterData.taxRate'),
      width: 120,
      valueFormatter: (v: number) => formatPct(v),
    },
    {
      field: 'isActive',
      headerName: t('masterData.active'),
      width: 100,
      valueFormatter: (v: boolean) => (v ? t('common.yes') : t('common.no')),
    },
    {
      field: 'isDefault',
      headerName: t('common.default'),
      width: 100,
      valueFormatter: (v: boolean) => (v ? t('common.yes') : t('common.no')),
    },
    {
      field: 'printText',
      headerName: t('masterData.printText'),
      flex: 1,
      minWidth: 120,
      valueFormatter: (v: string | null) => (v && v.length > 36 ? `${v.slice(0, 36)}…` : v || '–'),
    },
    {
      field: 'createdAt',
      headerName: t('common.created'),
      width: 150,
      valueFormatter: (v: string) => formatDateTime(v),
    },
    {
      field: 'updatedAt',
      headerName: t('common.updated'),
      width: 150,
      valueFormatter: (v: string) => formatDateTime(v),
    },
  ];

  const handleCreate = async () => {
    const rate = parseFloat(taxRate.replace(',', '.'));
    if (Number.isNaN(rate)) {
      showError(t('errors.generic'));
      return;
    }
    try {
      const res = await createRule({
        variables: {
          input: { name, taxRate: rate, isActive, isDefault, printText: printText || undefined },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      if (!res.data?.createTaxRule?.taxRule) {
        showError(t('errors.generic'));
        return;
      }
      showSuccess(t('masterData.created'));
      setOpen(false);
      setName('');
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 180px)' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h4">{t('masterData.taxRules')}</Typography>
        {isSuperAdmin(user) && (
          <Button variant="contained" onClick={() => setOpen(true)}>
            {t('masterData.createTaxRule')}
          </Button>
        )}
      </Box>

      <DataTable<Row>
        enableMobileCards
        columns={columns}
        data={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={rows.length}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        paginationMode="client"
        onRowClick={(row) => navigate(`/tax-rules/${row.id}`)}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('masterData.createTaxRule')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('common.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label={t('masterData.taxRate')}
            value={taxRate}
            onChange={(e) => setTaxRate(e.target.value)}
            fullWidth
            helperText={t('masterData.taxRateHint')}
          />
          <TextField
            label={t('masterData.printText')}
            value={printText}
            onChange={(e) => setPrintText(e.target.value)}
            fullWidth
          />
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label={t('masterData.active')}
          />
          <FormControlLabel
            control={
              <Switch checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
            }
            label={t('common.default')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !name.trim()}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
